import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge, RelationPicker, SearchableSelect } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { unwrapLookupItems, toInt } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldCustom, FieldToggle, useIsViewMode,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';
import CompanyProfileTab from './representative/CompanyProfileTab';
import LinkedTuccarsTab from './representative/LinkedTuccarsTab';
import DesignsTab from './representative/DesignsTab';

const API_BASE = '/api/representatives';

const STATUS_META = {
  1: { label: 'Aktif', tone: 'ok' },
  0: { label: 'Pasif', tone: 'neutral' },
  2: { label: 'Beklemede', tone: 'wait' },
  // #782 Silinen temsilci fiziksel silinmez: IsDeleted işaretlenir ve durumu İPTAL'e çekilir.
  // Pasif'ten AYRI bir değer — pasif kayıt listede durur ve geri açılabilir, iptal edilen düşer.
  3: { label: 'İptal', tone: 'bad' },
};
// #782 İptal (3) rozette gösterilir ama SEÇİLEBİLİR DEĞİLDİR: bu durumu yalnız silme akışı yazar
// (IsDeleted ile birlikte). Elle seçilebilseydi "durumu İptal ama kaydı silinmemiş" tutarsız bir
// temsilci doğardı — listede durur, rozeti silinmiş gibi görünürdü.
const STATUS_OPTIONS = [
  { value: '', label: 'Seçiniz' },
  ...Object.entries(STATUS_META)
    .filter(([value]) => Number(value) !== 3)
    .map(([value, meta]) => ({ value: Number(value), label: meta.label })),
];
const EMPTY_FORM = {
  textId: '', name: '', erpId: '', reference: '', plaka: '',
  groupId: '', salesManagerId: '', status: 1, vendorId: '', representativeTypeId: '',
  // 803: Sipariş Deposu / Satış Deposu — temsilcinin Distribütörüne (vendorId) tanımlı depolar.
  orderWarehouseId: '', salesWarehouseId: '',
  // Parametreler sekmesi: #784 günlük ziyaret + "Rut İçi Müşteri Mesafe Zorunluluğu" üçlüsü.
  requireDailyVisit: false, requireRouteDistance: false, routeDistanceMeters: '', routeDistanceBlocks: false,
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

export function RepresentativeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const isView = useIsViewMode() && !isNew; // #282 izle = salt-okunur
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Temsilci' : (isView ? 'Temsilci Görüntüle' : 'Temsilci Detayı'), 'Temsilci kartı bilgileri.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  // TÜCCAR PLASİYER — kart, seçili Temsilci Tipi'nin IsMerchant bayrağına göre iki sekme daha açar.
  // Bayrak TİPİN üzerindedir (adının değil): tenant tipi yeniden adlandırınca akış bozulmaz.
  const [activeTab, setActiveTab] = useState('genel');
  const profileRef = useRef(null);
  // #783 Dizaynlar sekmesi de kartın TEK Kaydet'ine bağlanır (CompanyProfileTab ile aynı desen).
  const designsRef = useRef(null);

  // "Ana Ticari Kimlik" anahtarı GENEL BİLGİLER'de durur: kullanıcı, tipi Tüccar seçtiği anda bu
  // temsilcinin kendi kimliği mi yoksa başkasının kimliği altında mı çalıştığını GÖRMELİDİR — bu
  // bilgi, kartın geri kalanının (VKN'yi kim girer, ayarlar nereden gelir) anlamını belirler.
  // Bağlı Tüccarlar sekmesine gömülü olsaydı, oraya girmeyen kullanıcı hiç fark etmezdi.
  const [hier, setHier] = useState(null);      // null = henüz bilinmiyor
  const [promoteConfirm, setPromoteConfirm] = useState(false);
  const [hierKey, setHierKey] = useState(0);   // artınca alt sekmeler kendini tazeler

  // Lookup'lar (RelationPicker + etiketler)
  const [vendors, setVendors] = useState([]);
  const [salesManagers, setSalesManagers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [representativeTypes, setRepresentativeTypes] = useState([]);
  // 803: Depo seçenekleri (Sipariş/Satış Deposu) — Distribütöre (vendorId) göre filtrelenir.
  const [warehouses, setWarehouses] = useState([]);

  // #115 (ters yön): bu temsilciye atanabilecek mobil kullanıcılar + seçili/ilk atama.
  // Temsilci formundan ayrı tutulur (rowToForm formu komple değiştirdiği için clobber olmasın).
  const [mobileUsers, setMobileUsers] = useState([]);
  const [assignedUserId, setAssignedUserId] = useState('');
  const [initialUserId, setInitialUserId] = useState('');

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // ── Lookup etiket çözücüler (listedeki mantıkla birebir) ──
  const getVendorLabel = useCallback((vid) => {
    const v = vendors.find((item) => item.id === vid);
    if (!v) return '-';
    return v.name ? `${v.textId || ''} - ${v.name}`.trim().replace(/^- /, '') : v.textId || '-';
  }, [vendors]);
  const getGroupLabel = useCallback((gid) => {
    const g = groups.find((item) => item.id === gid);
    if (!g) return '-';
    return g.description ? `${g.textId || ''} - ${g.description}`.trim().replace(/^- /, '') : g.textId || '-';
  }, [groups]);
  const getSalesManagerLabel = useCallback((mid) => {
    const m = salesManagers.find((item) => item.id === mid);
    if (!m) return '-';
    return m.description ? `${m.textId || ''} - ${m.description}`.trim().replace(/^- /, '') : m.textId || '-';
  }, [salesManagers]);

  // Lookup'ları yükle
  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/vendors/Select', { headers }).then((res) => res.ok ? res.json() : []),
      fetch('/api/representativegroups/Select', { headers }).then((res) => res.ok ? res.json() : []),
      fetch('/api/salesmanagers/Select', { headers }).then((res) => res.ok ? res.json() : []),
      fetch('/api/representativetypes/Select', { headers }).then((res) => res.ok ? res.json() : []),
      fetch('/api/warehouses/Select', { headers }).then((res) => res.ok ? res.json() : []),
    ]).then(([vendorData, groupData, salesManagerData, typeData, warehouseData]) => {
      setVendors(unwrapLookupItems(vendorData));
      setGroups(unwrapLookupItems(groupData));
      setSalesManagers(unwrapLookupItems(salesManagerData));
      setRepresentativeTypes(unwrapLookupItems(typeData));
      setWarehouses(unwrapLookupItems(warehouseData));
    }).catch(() => {});
  }, []);

  const rowToForm = (r) => ({
    textId: pick(r, 'textId', 'TextId') ?? '',
    name: pick(r, 'name', 'Name') ?? '',
    erpId: pick(r, 'erpId', 'ErpId') ?? '',
    reference: pick(r, 'reference', 'Reference') ?? '',
    plaka: pick(r, 'plaka', 'Plaka') ?? '',
    groupId: pick(r, 'groupId', 'GroupId') ?? '',
    salesManagerId: pick(r, 'salesManagerId', 'SalesManagerId') ?? '',
    status: pick(r, 'status', 'Status') ?? '',
    vendorId: pick(r, 'vendorId', 'VendorId') ?? '',
    representativeTypeId: pick(r, 'representativeTypeId', 'RepresentativeTypeId') ?? '',
    // 803: backend alanları (paralel ajan ekliyor). Yoksa boş kalır.
    orderWarehouseId: pick(r, 'orderWarehouseId', 'OrderWarehouseId') ?? '',
    salesWarehouseId: pick(r, 'salesWarehouseId', 'SalesWarehouseId') ?? '',
    // #784 Parametreler sekmesi
    requireDailyVisit: pick(r, 'requireDailyVisit', 'RequireDailyVisit') === true,
    // "Rut İçi Müşteri Mesafe Zorunluluğu" — mobil "Ziyarete Başla" kapısı bu üçlüyü okur.
    requireRouteDistance: pick(r, 'requireRouteDistance', 'RequireRouteDistance') === true,
    routeDistanceMeters: pick(r, 'routeDistanceMeters', 'RouteDistanceMeters') ?? '',
    routeDistanceBlocks: pick(r, 'routeDistanceBlocks', 'RouteDistanceBlocks') === true,
  });

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    // Önce listeden gelen satır (location.state.row) varsa onu kullan; ardından sunucudan tazele.
    if (location.state?.row) {
      const sr = location.state.row;
      setForm(rowToForm(sr));
      setAudit({ createdBy: pick(sr, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(sr, 'createdDate', 'CreatedDate'), updatedBy: pick(sr, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(sr, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    }
    setLoading(!location.state?.row);
    try {
      const res = await fetch(`${API_BASE}/GetById?Id=${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Temsilci alınamadı (${res.status})`);
      const r = unwrap(await res.json());
      if (r) {
        setForm(rowToForm(r));
        setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
      }
    } catch (err) {
      if (!location.state?.row) showToast('error', err.message || 'Temsilci alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // #115 (ters yön): atanabilir mobil kullanıcıları yükle + bu temsilciye atalı olanı preselect.
  useEffect(() => {
    if (isNew || !id) { setMobileUsers([]); setAssignedUserId(''); setInitialUserId(''); return; }
    fetch(`${API_BASE}/unassigned-users?representativeId=${id}`, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : []))
      .then((json) => {
        const data = unwrap(json) || [];
        const list = Array.isArray(data) ? data : [];
        setMobileUsers(list);
        const current = list.find((u) => String(pick(u, 'representativeId', 'RepresentativeId') ?? '') === String(id));
        const curId = current ? String(pick(current, 'id', 'Id')) : '';
        setAssignedUserId(curId);
        setInitialUserId(curId);
      })
      .catch(() => setMobileUsers([]));
  }, [id, isNew]);

  // #181 Temsilci Tipi seçenekleri — "Satış Temsilcisi Tipi" tanımlarından beslenir.
  const representativeTypeOptions = useMemo(() => [
    { value: '', label: 'Seçiniz' },
    ...representativeTypes.map((t) => {
      const id = pick(t, 'id', 'Id');
      const name = pick(t, 'name', 'Name') || `#${pick(t, 'code', 'Code') ?? id}`;
      const code = pick(t, 'code', 'Code');
      return { value: id, label: code != null && code !== '' ? `${code} - ${name}` : name };
    }),
  ], [representativeTypes]);

  // 803: Sipariş/Satış Deposu seçenekleri — temsilcinin Distribütörüne (vendorId)
  // tanımlı depolar. Vendor seçili değilse boş. Kayıtlı değer listede yoksa (eski/farklı vendor) korunur.
  const warehouseOptions = useMemo(() => {
    const whVendorId = (w) => String(w.vendorId ?? w.VendorId ?? '');
    const whId = (w) => String(w.id ?? w.Id ?? '');
    const whName = (w) => (w.name ?? w.Name ?? w.textId ?? w.TextId ?? '');
    const list = form.vendorId
      ? warehouses.filter((w) => whVendorId(w) === String(form.vendorId))
      : [];
    const build = (current) => {
      const opts = [
        { value: '', label: form.vendorId ? 'Seçiniz' : 'Önce Distribütör seçin' },
        ...list.map((w) => ({
          value: whId(w),
          label: `${whName(w)}${w.textId || w.TextId ? ` (${w.textId || w.TextId})` : ''}`,
        })),
      ];
      if (current && !opts.some((o) => o.value === String(current))) {
        opts.push({ value: String(current), label: `${current} (mevcut)` });
      }
      return opts;
    };
    return { order: build(form.orderWarehouseId), sales: build(form.salesWarehouseId) };
  }, [warehouses, form.vendorId, form.orderWarehouseId, form.salesWarehouseId]);

  // Seçili tip tüccar mı? Kartın firma / e-belge / bağlı tüccar sekmelerinin TEK kapısı.
  const isMerchant = useMemo(() => {
    if (!form.representativeTypeId) return false;
    const t = representativeTypes.find((x) => String(pick(x, 'id', 'Id')) === String(form.representativeTypeId));
    return !!pick(t, 'isMerchant', 'IsMerchant');
  }, [representativeTypes, form.representativeTypeId]);

  // Tüccar sekmeleri KAYDEDİLMİŞ bir temsilci ister: firma profili ve hiyerarşi, temsilcinin id'sine
  // asılıdır. Yeni kartta sekmeler görünür ama kilitlidir — gizlemek, kullanıcıya "bu tipte böyle bir
  // şey yok" izlenimi verirdi; oysa yalnız SIRA meselesi.
  const tuccarTabsReady = isMerchant && !isNew;

  const tabs = useMemo(() => {
    const base = [{ key: 'genel', label: 'Genel Bilgiler', icon: 'user' }];
    const merchantTabs = isMerchant
      ? [
        { key: 'firma', label: 'Firma ve E-Belge', icon: 'box', disabled: !tuccarTabsReady },
        { key: 'bagli', label: 'Bağlı Tüccarlar', icon: 'user', disabled: !tuccarTabsReady },
      ]
      : [];
    // #783/#784 Dizaynlar ve Parametreler HER temsilcide vardır (tüccar olması gerekmez).
    // #783 Dizaynlar KAYDEDİLMİŞ temsilci ister — atamalar temsilcinin id'sine asılıdır; yeni kartta
    // sekme görünür ama kilitlidir (tüccar sekmelerindeki desen).
    return [
      ...base,
      ...merchantTabs,
      { key: 'dizaynlar', label: 'Dizaynlar', icon: 'layers', disabled: isNew },
      { key: 'parametreler', label: 'Parametreler', icon: 'list' },
    ];
  }, [isMerchant, tuccarTabsReady, isNew]);

  // Tip tüccardan standarda dönerse açık bir TÜCCAR sekmesinde kalınmamalı ('parametreler' hariç —
  // o sekme tipe bağlı değildir).
  useEffect(() => {
    if (!isMerchant && (activeTab === 'firma' || activeTab === 'bagli')) setActiveTab('genel');
  }, [isMerchant, activeTab]);

  // Ticari kimlik hiyerarşisi — "Ana Ticari Kimlik" anahtarının durumu buradan gelir.
  useEffect(() => {
    if (!tuccarTabsReady) { setHier(null); return; }
    fetch(`/api/RepresentativeIdentity/linked-tuccars/${id}`, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => setHier(unwrap(json) || null))
      .catch(() => setHier(null));
  }, [tuccarTabsReady, id, hierKey]);

  // Anahtarı AÇMAK = bağı koparmak (spec §5.3). Kapatma yönü kart üzerinden YAPILAMAZ: hangi anaya
  // bağlanacağı bilgisi burada yoktur — bağlama, ana tüccarın kartından yapılır.
  const promoteToPrimary = async () => {
    setPromoteConfirm(false);
    try {
      const res = await fetch(`/api/RepresentativeIdentity/primary-identity/${id}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ isPrimaryIdentity: true }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || 'İşlem başarısız');
      showToast('success', 'Bağ çözüldü. Artık kendi ticari kimliği — firma ve e-belge ayarlarını tanımlayın.');
      setHierKey((k) => k + 1);   // hiyerarşi + her iki tüccar sekmesi tazelensin
    } catch (err) {
      showToast('error', err.message);
    }
  };

  const mobileUserOptions = useMemo(() => [
    { value: '', label: 'Atanmadı' },
    ...mobileUsers.map((u) => {
      const email = pick(u, 'email', 'Email');
      const name = pick(u, 'fullName', 'FullName') || email || `#${pick(u, 'id', 'Id')}`;
      return { value: String(pick(u, 'id', 'Id')), label: email ? `${name} (${email})` : name };
    }),
  ], [mobileUsers]);

  // Klonla: /representatives/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildPayload = () => ({
    textId: form.textId,
    name: form.name,
    erpId: form.erpId || null,
    reference: form.reference || null,
    plaka: form.plaka || null,
    groupId: form.groupId || null,
    salesManagerId: form.salesManagerId || null,
    status: toInt(form.status),
    vendorId: form.vendorId,
    representativeTypeId: form.representativeTypeId || null,
    // 803: iki alan da payload'a girer (backend nullable Guid). Aynı depo ikisine de seçilebilir.
    orderWarehouseId: form.orderWarehouseId || null,
    salesWarehouseId: form.salesWarehouseId || null,
    // #784 Günlük Ziyaret Zorunluluğu (Parametreler sekmesi)
    requireDailyVisit: !!form.requireDailyVisit,
    // "Rut İçi Müşteri Mesafe Zorunluluğu". Bayrak kapalıysa eşik ve aksiyon GÖNDERİLMEZ (null/false):
    // kapalı bir kuralın yanında duran eski eşik, sonradan açıldığında sürpriz davranış yaratırdı.
    requireRouteDistance: !!form.requireRouteDistance,
    routeDistanceMeters: form.requireRouteDistance ? (toInt(form.routeDistanceMeters) || null) : null,
    routeDistanceBlocks: form.requireRouteDistance ? !!form.routeDistanceBlocks : false,
  });

  // andNew: kaydet ama listeye dönme — formu sıfırlayıp boş yeni kayda hazırlan.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.textId.trim()) { showToast('error', 'Temsilci kodu boş olamaz'); return; }
    if (!form.name.trim()) { showToast('error', 'Temsilci adı boş olamaz'); return; }
    if (!form.vendorId) { showToast('error', 'Distribütör seçimi zorunludur'); return; }
    if (!form.representativeTypeId) { showToast('error', 'Temsilci tipi seçimi zorunludur'); return; }
    // #854 Depolar ZORUNLU. Boş bırakıldığında saha tarafı sessizce bozuluyordu: mobil belge
    // deposunu bu iki alandan çözüyor (#846) ve çözemeyince "cihazdaki tüm depoların toplamı"
    // fallback'ine düşüp olmayan stoğu gösteriyordu. Kaynağı burada kapatmak, aşağıda tahmin
    // yürütmekten iyidir. (Eksik alanlar ayrıca kırmızı çizilir — bkz. tried + req.)
    if (!form.orderWarehouseId) { showToast('error', 'Sipariş Deposu seçimi zorunludur'); return; }
    if (!form.salesWarehouseId) { showToast('error', 'Satış Deposu seçimi zorunludur'); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(isNew ? payload : { id, ...payload }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || (isNew ? 'Kayıt eklenemedi' : 'Kayıt güncellenemedi'));
      if (isNew) {
        if (andNew) {
          showToast('success', 'Temsilci eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Temsilci başarıyla eklendi');
        // Add yeni id döndürmüyor; listeye dön.
        setTimeout(() => navigate('/representatives'), 400);
        return;
      }
      // #115 (ters yön): mobil kullanıcı ataması değiştiyse uygula (temsilci kaydından bağımsız endpoint).
      if (assignedUserId !== initialUserId) {
        const ar = await fetch(`${API_BASE}/${id}/assign-user`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({ userId: assignedUserId ? Number(assignedUserId) : null }),
        });
        const ab = await ar.json().catch(() => null);
        if (!ar.ok) throw new Error(pick(ab, 'Message', 'message') || 'Kullanıcı ataması başarısız');
        setInitialUserId(assignedUserId);
      }

      // TÜCCAR PLASİYER — "Firma ve E-Belge" sekmesi AYNI Kaydet ile yazılır. Sekmeye kendi Kaydet'ini
      // koymak, kullanıcıya "hangi Kaydet neyi kaydediyor" sorusunu sordururdu. Sekme salt okunursa
      // (alt tüccar) save() sessizce geçer.
      // ⚠ Temsilci kaydından SONRA: tip bu turda tüccara çevrildiyse profil PUT'u ancak o zaman kabul edilir.
      if (isMerchant && profileRef.current && !profileRef.current.isReadOnly()) {
        await profileRef.current.save();
      }

      // #783 Dizayn atamaları da AYNI Kaydet ile yazılır. Temsilci kaydından SONRA: yeni kartta
      // atama yapılamaz (sekme kilitli), mevcut kartta ise sıranın önemi yok — ayrı uç.
      if (designsRef.current && !designsRef.current.isReadOnly()) {
        await designsRef.current.save();
      }

      showToast('success', 'Temsilci başarıyla güncellendi');
      setTimeout(() => navigate('/representatives'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın form bilgilerini taşıyarak yeni-kayıt ekranını aç (hiçbir şey kaydedilmez).
  const handleClone = () => {
    navigate('/representatives/new', { state: { clone: { ...form } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || 'Kayıt silinemedi');
      showToast('success', 'Temsilci başarıyla silindi');
      setTimeout(() => navigate('/representatives'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme işlemi sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const title = isNew ? 'Yeni Temsilci' : (form.name || 'Temsilci Detayı');
  const statusMeta = STATUS_META[form.status];
  const statusBadge = !isNew && statusMeta
    ? <StatusBadge tone={statusMeta.tone}>{statusMeta.label}</StatusBadge>
    : null;

  // #282 İzle modunda salt-okunur: yalnız "Kapat" + "Düzenle".
  const actions = isView ? (
    <>
      <button className="dps-btn ghost" onClick={() => navigate('/representatives')}>Kapat</button>
      <button className="dps-btn primary" onClick={() => navigate(`/representatives/${id}`, { state: { row: location.state?.row, view: false }, replace: true })}>Düzenle</button>
    </>
  ) : (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/representatives')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni temsilci aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      readOnly={isView}
      crumb={[{ label: 'Temsilciler', to: '/representatives' }, { label: isNew ? 'Yeni Temsilci' : (isView ? 'Temsilci Görüntüle' : 'Temsilci Detayı'), current: true }]}
      status={statusBadge}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'Representative', entityId: id }}
      loading={loading}
      tabs={tabs.length > 1 ? tabs : undefined}
      activeTab={activeTab}
      onTab={setActiveTab}
    >
      {/* Sekme panelleri MONTE KALIR (display:none) — sökülselerdi kullanıcı "Firma"da yazdıklarını
          "Genel"e geçince kaybederdi ve tek Kaydet iki sekmeyi birlikte yazamazdı. */}
      <div className="dps-form" style={{ display: activeTab === 'genel' ? undefined : 'none' }}>
        {/* TÜCCAR PLASİYER — "Ana Ticari Kimlik" anahtarı, kartın SAĞ ÜST köşesinde.
            Bir form alanı DEĞİL, kartın ROLÜNÜ söyleyen bir durum: bu temsilci kendi VKN'siyle mi
            belge kesiyor, yoksa ayarlar bir ana tüccardan mı geliyor? Grid'in içine konsaydı
            "doldurulacak bir alan" gibi okunurdu; köşede, Durum rozetinin dilinde duruyor.

            Anahtar YALNIZ AÇILABİLİR (alt → ana = bağı kopar). Kapatma yönü kart üzerinde
            anlamsızdır: "alt tüccar yap" hemen "KİMİN altına?" sorusunu doğurur ve o seçim ana
            tüccarın "Bağlı Tüccarlar" sekmesinde yapılır. Kilidin sebebi not bandında yazar —
            tepkisiz bir anahtarla karşılaşılmaz. */}
        <Section
          icon="user"
          title="Temsilci Bilgileri"
          subtitle="Kimlik, organizasyon ve çalışma bilgileri"
          right={isMerchant ? (
            <FieldToggle
              form={{ primary: isNew || !hier || hier.isPrimaryIdentity }}
              set={() => {}}
              k="primary"
              label="Ana Ticari Kimlik"
              disabled={!canEdit || isView || isNew || !hier || hier.isPrimaryIdentity}
              onChange={() => setPromoteConfirm(true)}
            />
          ) : undefined}
          note={isMerchant ? (
            isNew
              ? 'Her tüccar varsayılan olarak kendi ana ticari kimliğidir. Başka bir tüccarın kimliğine bağlamak için önce bu kartı kaydedin.'
              : (!hier || hier.isPrimaryIdentity)
                ? 'Ana ticari kimlik: kendi VKN’si ve e-belge ayarlarıyla belge keser. Bu temsilciyi bir başkasının kimliğine bağlamak için, o ana tüccarın kartındaki “Bağlı Tüccarlar” sekmesini kullanın.'
                : `Alt tüccar: firma ve e-belge ayarları “${hier.parentCommercialTitle || hier.parentRepresentativeName}” ana tüccarından geliyor. Anahtarı açarsanız bağ kopar ve kendi ticari kimliğini tanımlaması gerekir.`
          ) : undefined}
        >
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="textId" label="Kod" req mono />
            <FieldText form={form} set={set} tried={tried} k="name" label="Ad" req />
            <FieldCustom label="Distribütör" req invalid={tried && !form.vendorId}>
              <RelationPicker
                config={RELATION_CONFIGS.vendor}
                rows={vendors}
                value={form.vendorId}
                selectedLabel={form.vendorId ? getVendorLabel(form.vendorId) : ''}
                onChange={(val) => setForm((c) => ({ ...c, vendorId: val, orderWarehouseId: '', salesWarehouseId: '' }))}
                placeholder="Seçiniz"
              />
            </FieldCustom>
            <FieldCustom label="Satış Müdürü">
              <RelationPicker
                config={RELATION_CONFIGS.salesManager}
                rows={salesManagers}
                value={form.salesManagerId}
                selectedLabel={form.salesManagerId ? getSalesManagerLabel(form.salesManagerId) : ''}
                onChange={(val) => set('salesManagerId', val)}
                placeholder="Seçiniz"
              />
            </FieldCustom>
            <FieldCustom label="Temsilci Grubu">
              <RelationPicker
                config={RELATION_CONFIGS.representativeGroup}
                rows={groups}
                value={form.groupId}
                selectedLabel={form.groupId ? getGroupLabel(form.groupId) : ''}
                onChange={(val) => set('groupId', val)}
                placeholder="Seçiniz"
              />
            </FieldCustom>
            <FieldSelect form={form} set={set} tried={tried} k="representativeTypeId" label="Temsilci Tipi" options={representativeTypeOptions} req />
            <FieldSelect form={form} set={set} k="status" label="Durum" options={STATUS_OPTIONS} />
            <FieldText form={form} set={set} k="plaka" label="Plaka" />
            {/* 803: Distribütöre tanımlı depolar. Aynı depo iki alana da seçilebilir. */}
            {/* #854 Zorunlu: mobil, sipariş girişinde SİPARİŞ deposunun, irsaliye/faturada SATIŞ
                deposunun stoğunu gösterir (#846). Boş kalırsa saha yanlış stok görür. */}
            <FieldSelect
              form={form} set={set} tried={tried} k="orderWarehouseId" label="Sipariş Deposu" req
              options={warehouseOptions.order}
              disabled={!form.vendorId}
              hint={!form.vendorId ? 'Depo seçmek için önce Distribütör seçin.' : undefined}
            />
            <FieldSelect
              form={form} set={set} tried={tried} k="salesWarehouseId" label="Satış Deposu" req
              options={warehouseOptions.sales}
              disabled={!form.vendorId}
              hint={!form.vendorId ? 'Depo seçmek için önce Distribütör seçin.' : undefined}
            />
            {/* #753 Kullanıcı seçimi ARAMALI. Düz select 700-800 kullanıcıda kullanılamaz hâle
                geliyordu (talep: "diğer rehberlerdeki gibi arama özelliği olmalı"). Liste zaten
                cihaza yüklendiği için arama İSTEMCİ TARAFINDA yapılır — atanabilir kullanıcılar
                küçük ve kapılı bir kümedir (yalnız mobil rolü + atanmamışlar + bu temsilcinin
                mevcut kullanıcısı), sunucuya her tuşta gitmek gereksiz olurdu. */}
            {!isNew && (
              <FieldCustom label="Kullanıcı">
                <SearchableSelect
                  value={assignedUserId || ''}
                  selectedLabel={
                    mobileUserOptions.find((o) => o.value === String(assignedUserId || ''))?.label || ''
                  }
                  onChange={(v) => setAssignedUserId(v || '')}
                  disabled={!canEdit || isView}
                  placeholder="Ad veya e-posta ile ara…"
                  emptyOption="Atanmadı"
                  minChars={0}
                  fetchOptions={(q) => {
                    const needle = (q || '').trim().toLocaleLowerCase('tr');
                    const rows = mobileUserOptions.filter((o) => o.value !== '');
                    return Promise.resolve(
                      needle
                        ? rows.filter((o) => o.label.toLocaleLowerCase('tr').includes(needle))
                        : rows,
                    );
                  }}
                />
              </FieldCustom>
            )}
          </Grid>
        </Section>

        <Section icon="box" title="Kod & Referans" subtitle="ERP ve referans bilgileri">
          <Grid cols={2}>
            <FieldText form={form} set={set} k="erpId" label="ERP Kodu" mono />
            <FieldText form={form} set={set} k="reference" label="Referans" />
          </Grid>
        </Section>

        {/* Tüccar seçildi ama kart henüz kaydedilmedi — sekmeler neden kilitli, açıkça söylenir. */}
        {isMerchant && isNew && (
          <Section icon="info" title="Firma ve E-Belge ayarları kayıttan sonra açılır"
                   subtitle="Ticari kimlik, temsilcinin kaydına asılıdır">
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.7, color: 'var(--dps-ink-2)' }}>
              <strong>Tüccar</strong> tipini seçtiniz. Bu temsilciyi kaydettikten sonra{' '}
              <strong>Firma ve E-Belge</strong> ve <strong>Bağlı Tüccarlar</strong> sekmeleri açılır;
              oradan VKN, entegratör bilgileri ve belge görsellerini tanımlarsınız.
            </p>
          </Section>
        )}
      </div>

      {/* TÜCCAR PLASİYER — firma / e-belge ayarları (spec §3, §4) */}
      {tuccarTabsReady && (
        <div style={{ display: activeTab === 'firma' ? undefined : 'none' }}>
          <CompanyProfileTab
            ref={profileRef}
            repId={id}
            vendors={vendors}
            onToast={showToast}
            refreshKey={hierKey}
          />
        </div>
      )}

      {/* TÜCCAR PLASİYER — ticari kimlik hiyerarşisi (spec §5) */}
      {tuccarTabsReady && (
        <div style={{ display: activeTab === 'bagli' ? undefined : 'none' }}>
          <LinkedTuccarsTab
            repId={id}
            repName={form.name}
            canEdit={canEdit && !isView}
            onToast={showToast}
            refreshKey={hierKey}
            onHierarchyChange={setHier}
          />
        </div>
      )}

      {/* #783 DİZAYNLAR — belge başlığı → belge dizaynı atamaları. Kartın TEK Kaydet'i ile yazılır
          (designsRef). Yeni kartta sekme kilitli olduğu için burada da yalnız kayıtlıyken monte edilir. */}
      {!isNew && (
        <div className="dps-form" style={{ display: activeTab === 'dizaynlar' ? undefined : 'none' }}>
          <DesignsTab ref={designsRef} repId={id} readOnly={!canEdit || isView} />
        </div>
      )}

      {/* #784 PARAMETRELER — temsilcinin çalışma kuralları. Panel MONTE KALIR (display:none):
          buradaki değişiklik kartın TEK Kaydet'iyle, Genel sekmesiyle birlikte yazılır. */}
      <div className="dps-form" style={{ display: activeTab === 'parametreler' ? undefined : 'none' }}>
        <Section
          icon="list"
          title="Temsilci Parametreleri"
          subtitle="Temsilcinin saha akışını etkileyen kurallar"
        >
          <Grid cols={2}>
            <FieldToggle
              form={form}
              set={set}
              k="requireDailyVisit"
              label="Günlük Ziyaret Zorunluluğu"
              disabled={!canEdit || isView}
              hint="Açıkken temsilci, o günkü rutundaki her müşteri için sipariş ya da satamama nedeni girmeden günü bitiremez."
            />
          </Grid>

          {/* #784 (2. tur) MESAFE KURALININ ÜÇ ALANI TEK BİR ÇERÇEVEDE.
              Üçü de Grid'in ayrı hücrelerindeydi ve "birbirinden bağımsız üç ayrı parametre" gibi
              duruyordu; oysa metre ve aşım davranışı yalnız bayrak açıkken anlamlı, ona bağlı
              alanlardır. Aynı katman içine alındılar: dış çerçeve bayrağı, iç blok ona bağlı
              ayarları taşır ve yalnız kural açıkken açılır. */}
          <div style={{
            border: '1px solid var(--border-color, rgba(15,23,42,0.10))',
            borderRadius: 10,
            padding: '14px 16px',
            marginTop: 12,
            background: 'var(--bg-subtle, rgba(148,163,184,0.06))',
          }}>
            <FieldToggle
              form={form}
              set={set}
              k="requireRouteDistance"
              label="Rut İçi Müşteri Mesafe Zorunluluğu"
              disabled={!canEdit || isView}
              hint="Açıkken mobilde “Ziyarete Başla”, temsilcinin konumu ile müşterinin konumu arasındaki mesafeyi denetler. Yalnız BUGÜN rutundaki müşteriler için geçerlidir."
            />
            {form.requireRouteDistance && (
              <div style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: '1px dashed var(--border-color, rgba(15,23,42,0.14))',
              }}>
                <Grid cols={2}>
                  <FieldText
                    form={form}
                    set={set}
                    k="routeDistanceMeters"
                    label="İzin Verilen Maksimum Mesafe (metre)"
                    num
                    placeholder="Örn. 100"
                    readOnly={!canEdit || isView}
                    hint="Boş bırakılırsa mesafe kontrolü yapılmaz."
                  />
                  <FieldToggle
                    form={form}
                    set={set}
                    k="routeDistanceBlocks"
                    label={form.routeDistanceBlocks ? 'Aşımda: İŞLEMİ KES' : 'Aşımda: UYARI VER'}
                    disabled={!canEdit || isView}
                    hint={form.routeDistanceBlocks
                      ? 'Mesafe aşılırsa ziyaret BAŞLATILAMAZ: “Müşteriye belirlenen mesafeden uzaksınız. Ziyaret başlatılamaz!”'
                      : 'Mesafe aşılırsa temsilci yalnız uyarılır, ziyaret yine başlar: “Noktaya belirli mesafeden uzaksınız. Yine de ziyaret başlatılıyor.”'}
                  />
                </Grid>
              </div>
            )}
          </div>
        </Section>
      </div>

      {/* Ana Ticari Kimlik — bağı koparma onayı (yıkıcı: profil sıfırlanır) */}
      {promoteConfirm && hier && (
        <SubModal
          title="Ana Ticari Kimlik Yap"
          maxWidth={520}
          onClose={() => setPromoteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setPromoteConfirm(false)}>İptal</button>
              <button className="dps-btn primary" onClick={promoteToPrimary}>Evet, Devam Et</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.7 }}>
            <strong>{form.name}</strong> temsilcisinin{' '}
            <strong>{hier.parentCommercialTitle || hier.parentRepresentativeName}</strong> ile bağı
            kopacak ve kendi ticari kimliği olacak.
            <br /><br />
            Firma ve e-belge formu <strong>boş açılır</strong>; kendi VKN’si ve entegratör bilgisi
            tanımlanana kadar <strong>belge kesemez</strong>.
          </p>
        </SubModal>
      )}

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.name}”</strong> temsilcisini silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default RepresentativeDetailPage;
